/**
 * BookingSettings Step Component
 *
 * Step 6 of listing wizard: booking configuration.
 */

import React from 'react';
import { ListingFormData } from '../../hooks';

interface BookingSettingsProps {
  data: ListingFormData;
  onChange: (updates: Partial<ListingFormData>) => void;
}

const checkInTimes = [
  '08:00', '09:00', '10:00', '11:00', '12:00',
  '13:00', '14:00', '15:00', '16:00', '17:00', '18:00',
];

const checkOutTimes = [
  '08:00', '09:00', '10:00', '11:00', '12:00',
  '13:00', '14:00', '15:00',
];

const advanceNoticeOptions = [
  { value: 0, label: 'Same day' },
  { value: 1, label: '1 day' },
  { value: 2, label: '2 days' },
  { value: 3, label: '3 days' },
  { value: 7, label: '1 week' },
];

const availabilityWindowOptions = [
  { value: 30, label: '1 month' },
  { value: 60, label: '2 months' },
  { value: 90, label: '3 months' },
  { value: 180, label: '6 months' },
  { value: 365, label: '1 year' },
];

export const BookingSettings: React.FC<BookingSettingsProps> = ({
  data,
  onChange,
}) => {
  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Booking settings
        </h3>
        <p className="text-gray-600">
          Control how and when guests can book your property.
        </p>
      </div>

      {/* Instant Booking */}
      <div className="bg-gray-50 rounded-xl p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <ZapIcon className="w-5 h-5 text-yellow-500" />
              <h4 className="font-semibold text-gray-900">Instant Book</h4>
            </div>
            <p className="text-sm text-gray-600 mt-2">
              Let guests book instantly without waiting for your approval. Properties
              with Instant Book tend to get more bookings.
            </p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer ml-4">
            <input
              type="checkbox"
              checked={data.instantBooking}
              onChange={(e) => onChange({ instantBooking: e.target.checked })}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-500"></div>
          </label>
        </div>
      </div>

      {/* Stay Duration */}
      <div>
        <h4 className="font-medium text-gray-900 mb-4">Stay duration</h4>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum nights
            </label>
            <select
              value={data.minNights}
              onChange={(e) => onChange({ minNights: parseInt(e.target.value) })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {[1, 2, 3, 4, 5, 7, 14, 28, 30].map((n) => (
                <option key={n} value={n}>
                  {n} {n === 1 ? 'night' : 'nights'}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Maximum nights
            </label>
            <select
              value={data.maxNights}
              onChange={(e) => onChange({ maxNights: parseInt(e.target.value) })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {[7, 14, 28, 30, 60, 90, 180, 365].map((n) => (
                <option key={n} value={n}>
                  {n} nights {n >= 30 && `(${Math.round(n / 30)} ${n >= 60 ? 'months' : 'month'})`}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Check-in/Check-out Times */}
      <div>
        <h4 className="font-medium text-gray-900 mb-4">Check-in & Check-out</h4>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Check-in time
            </label>
            <select
              value={data.checkInTime}
              onChange={(e) => onChange({ checkInTime: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {checkInTimes.map((time) => (
                <option key={time} value={time}>
                  {formatTime(time)} or later
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Check-out time
            </label>
            <select
              value={data.checkOutTime}
              onChange={(e) => onChange({ checkOutTime: e.target.value })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {checkOutTimes.map((time) => (
                <option key={time} value={time}>
                  {formatTime(time)} or earlier
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Advance Notice & Availability */}
      <div>
        <h4 className="font-medium text-gray-900 mb-4">Booking window</h4>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Advance notice required
            </label>
            <select
              value={data.advanceNotice}
              onChange={(e) => onChange({ advanceNotice: parseInt(e.target.value) })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {advanceNoticeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              How much notice you need before a guest can check in
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Availability window
            </label>
            <select
              value={data.availabilityWindow}
              onChange={(e) => onChange({ availabilityWindow: parseInt(e.target.value) })}
              className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {availabilityWindowOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              How far in advance guests can book
            </p>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="bg-primary-50 rounded-xl p-4">
        <h4 className="font-medium text-gray-900 mb-2">Booking summary</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>
            • {data.instantBooking ? 'Instant Book enabled' : 'Manual approval required'}
          </li>
          <li>• Guests can stay {data.minNights}-{data.maxNights} nights</li>
          <li>• Check-in: {formatTime(data.checkInTime)}, Check-out: {formatTime(data.checkOutTime)}</li>
          <li>• Bookings can be made up to {
            availabilityWindowOptions.find(o => o.value === data.availabilityWindow)?.label
          } in advance</li>
        </ul>
      </div>
    </div>
  );
};

// Helper function
const formatTime = (time: string) => {
  const [hours, minutes] = time.split(':');
  const hour = parseInt(hours);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return `${displayHour}:${minutes} ${ampm}`;
};

// Icon
const ZapIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);

export default BookingSettings;
